import { useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { entities, modules } from "byzantine";
import { useLocalization } from "@fluent/react";
import { Row, useBreakpoints, Tabs, Alert } from "@narmi/design_system";
import {
  useFormattedPaginatedDualApprovals,
  useFormattedPaginatedHistoryDualApprovals,
  useFormattedPaginatedTemplateDualApprovals,
  useDualApprovalTotal,
  useHistoryDualApprovalTotal,
  useTemplateDualApprovalTotal,
} from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/hooks";
import Pagination from "byzantine/src/Pagination";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import { areAchTemplatesAvailable } from "../Templates/constants";
import PageLayout from "../../src/components/PageLayout";
import HistoryTable from "./History/HistoryTable";
import PendingTable from "./Pending/PendingTable";
import TemplatePendingTable from "./Templates/TemplatePendingTable";
import PageNavigation from "../page_navigation/PageNavigation";
import styles from "./DualApproval.module.scss";
import {
  DUAL_APPROVALS_ROUTE,
  DUAL_APPROVALS_TEMPLATES_PENDING_ROUTE,
} from "./routes";

const DualApprovalPermissionsAlert = () => {
  const { l10n } = useLocalization();
  const permissionedErrorMessage = l10n.getString(
    "approval-requires-multiple-permissioned-users",
    { br: "\n" },
    "You’re the only user with approvals permissions, so any items you submit can’t be approved or rejected by others.\n[Update user permissions](/manage_users)"
  );

  return (
    <div className={styles.permissionsAlert}>
      <Alert isActive isDismissable={false} kind="error">
        {permissionedErrorMessage
          .split("\n")
          .map((line: string, index: number) => (
            <>
              <ReactMarkdown
                className={styles.approvalPermissionMessageContent}
                key={index}
              >
                {line}
              </ReactMarkdown>
            </>
          ))}
      </Alert>
    </div>
  );
};

const DualApprovalContainer = () => {
  const PENDING_TAB_INDEX = 0;
  const PAGE_SIZE = 20;

  const { l10n } = useLocalization();
  const { m } = useBreakpoints();
  const navigate = useNavigate();
  const isHistoryTab = Boolean(useMatch(`/${DUAL_APPROVALS_ROUTE}/history/*`));
  const isTemplatesTab = Boolean(
    useMatch(
      `/${DUAL_APPROVALS_ROUTE}/${DUAL_APPROVALS_TEMPLATES_PENDING_ROUTE}/*`
    )
  );

  const { send: fetchPaginatedDualApprovals, loading } =
    entities.dualApprovals.useFetchPaginatedDualApprovals();
  const {
    send: fetchPaginatedTemplateDualApprovals,
    loading: templateLoading,
  } = entities.dualApprovals.useFetchPaginatedTemplateDualApprovals();
  const { send: fetchPaginatedHistoryDualApprovals, loading: historyLoading } =
    entities.dualApprovals.useFetchPaginatedHistoryDualApprovals();
  // Include hidden recipients so bulk payments created via ACH file import
  // (where the sender opted not to save the recipient) display the recipient
  // details to the approver. Hidden recipients are excluded from the default
  // recipients list, so without this they render blank in the bulk payment
  // review dialog. See PRO-1633.
  const { send: fetchRecipients } = entities.recipients.useFetchRecipients({
    includeHidden: true,
  });
  const {
    organizationHasMultipleApprovalUsers,
    loading: orgUsersLoading,
    isOrgUserWithEditPerms,
  } = modules.achPayments.useOrganizationHasMultipleApprovalUsers();
  const features = useUserFeatures();
  const achTemplatesEnabled = areAchTemplatesAvailable(features);

  const TEMPLATES_TAB_INDEX = achTemplatesEnabled ? 1 : -1;
  const HISTORY_TAB_INDEX = achTemplatesEnabled ? 2 : 1;

  const [pageNumber, setPageNumber] = useState(1);
  const [templatePageNumber, setTemplatePageNumber] = useState(1);
  const [historyPageNumber, setHistoryPageNumber] = useState(1);
  const getInitialTab = () => {
    if (isHistoryTab) return HISTORY_TAB_INDEX;
    if (isTemplatesTab) return TEMPLATES_TAB_INDEX;
    return PENDING_TAB_INDEX;
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  const dualApprovals = useFormattedPaginatedDualApprovals(pageNumber);
  const totalPendingApproval = useDualApprovalTotal();
  const templateDualApprovals =
    useFormattedPaginatedTemplateDualApprovals(templatePageNumber);
  const totalTemplatePendingApproval = useTemplateDualApprovalTotal();
  const historyDualApprovals =
    useFormattedPaginatedHistoryDualApprovals(historyPageNumber);
  const totalHistoryApproval = useHistoryDualApprovalTotal();

  useEffect(() => {
    fetchPaginatedDualApprovals(
      pageNumber,
      PAGE_SIZE,
      undefined,
      "BulkPaymentTemplateVersion"
    );
  }, [pageNumber, fetchPaginatedDualApprovals]);

  useEffect(() => {
    if (achTemplatesEnabled) {
      fetchPaginatedTemplateDualApprovals(templatePageNumber, PAGE_SIZE);
    }
  }, [
    templatePageNumber,
    fetchPaginatedTemplateDualApprovals,
    achTemplatesEnabled,
  ]);

  useEffect(() => {
    fetchPaginatedHistoryDualApprovals(historyPageNumber, PAGE_SIZE);
  }, [historyPageNumber, fetchPaginatedHistoryDualApprovals]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  useEffect(() => {
    if (isHistoryTab) {
      setSelectedTab(HISTORY_TAB_INDEX);
    } else if (isTemplatesTab) {
      setSelectedTab(TEMPLATES_TAB_INDEX);
    } else {
      setSelectedTab(PENDING_TAB_INDEX);
    }
  }, [isHistoryTab, isTemplatesTab, HISTORY_TAB_INDEX, TEMPLATES_TAB_INDEX]);

  const pagination = new Pagination({
    total: totalPendingApproval,
    page: pageNumber,
    pageSize: PAGE_SIZE,
    navigatePage: (page: number) => {
      setPageNumber(page);
    },
  });

  const templatePagination = new Pagination({
    total: totalTemplatePendingApproval,
    page: templatePageNumber,
    pageSize: PAGE_SIZE,
    navigatePage: (page: number) => {
      setTemplatePageNumber(page);
    },
  });

  const historyPagination = new Pagination({
    total: totalHistoryApproval,
    page: historyPageNumber,
    pageSize: PAGE_SIZE,
    navigatePage: (page: number) => {
      setHistoryPageNumber(page);
    },
  });

  const showDualApprovalsAlert =
    !organizationHasMultipleApprovalUsers &&
    !orgUsersLoading &&
    isOrgUserWithEditPerms;

  const handleTabChange = (tabId: number) => {
    if (tabId === HISTORY_TAB_INDEX) {
      navigate(`/${DUAL_APPROVALS_ROUTE}/history`);
    } else if (tabId === TEMPLATES_TAB_INDEX) {
      navigate(
        `/${DUAL_APPROVALS_ROUTE}/${DUAL_APPROVALS_TEMPLATES_PENDING_ROUTE}`
      );
    } else {
      navigate(`/${DUAL_APPROVALS_ROUTE}`);
    }
  };

  return (
    <PageLayout>
      {showDualApprovalsAlert ? <DualApprovalPermissionsAlert /> : null}
      <div className="margin--top--xxl margin--bottom--xl">
        {m ? (
          <Row>
            <Row.Item>
              <h1>{l10n.getString("heading-dual-approvals")}</h1>
            </Row.Item>
          </Row>
        ) : (
          <>
            <h1 className="margin--bottom--m">
              {l10n.getString("heading-dual-approvals")}
            </h1>
          </>
        )}
      </div>
      <Tabs onTabChange={handleTabChange} selectedIndex={selectedTab}>
        <Tabs.List>
          <Tabs.Tab
            label={l10n.getString("heading-pending-payments-count", {
              totalAwaitingApproval: totalPendingApproval,
            })}
            tabId="pending-approvals"
          />
          {achTemplatesEnabled ? (
            <Tabs.Tab
              label={l10n.getString(
                "heading-pending-templates-count",
                { totalAwaitingApproval: totalTemplatePendingApproval },
                `Templates pending (${totalTemplatePendingApproval})`
              )}
              tabId="templates-pending"
            />
          ) : null}
          <Tabs.Tab
            label={l10n.getString("heading-approval-history")}
            tabId="history-approvals"
          />
        </Tabs.List>
        <Tabs.Panel tabId="pending-approvals">
          {selectedTab === PENDING_TAB_INDEX ? (
            <>
              <PendingTable
                dualApprovals={dualApprovals}
                loading={loading}
                handleRefresh={() => {
                  fetchPaginatedHistoryDualApprovals(historyPageNumber, PAGE_SIZE);
                  return fetchPaginatedDualApprovals(
                    pageNumber,
                    PAGE_SIZE,
                    undefined,
                    "BulkPaymentTemplateVersion"
                  );
                }}
              />
              <PageNavigation
                pagination={pagination}
                marginClasses="margin--all--l"
              />
            </>
          ) : null}
        </Tabs.Panel>
        {achTemplatesEnabled ? (
          <Tabs.Panel tabId="templates-pending">
            {selectedTab === TEMPLATES_TAB_INDEX ? (
              <>
                <TemplatePendingTable
                  dualApprovals={templateDualApprovals}
                  loading={templateLoading}
                  handleRefresh={() => {
                    fetchPaginatedHistoryDualApprovals(historyPageNumber, PAGE_SIZE);
                    return fetchPaginatedTemplateDualApprovals(
                      templatePageNumber,
                      PAGE_SIZE
                    );
                  }}
                />
                <PageNavigation
                  pagination={templatePagination}
                  marginClasses="margin--all--l"
                />
              </>
            ) : null}
          </Tabs.Panel>
        ) : null}
        <Tabs.Panel tabId="history-approvals">
          {selectedTab === HISTORY_TAB_INDEX ? (
            <>
              <HistoryTable
                dualApprovals={historyDualApprovals}
                loading={historyLoading}
              />
              <PageNavigation
                pagination={historyPagination}
                marginClasses="margin--all--l"
              />
            </>
          ) : null}
        </Tabs.Panel>
      </Tabs>
    </PageLayout>
  );
};

export default DualApprovalContainer;
